import { Menu, MenuItem, Tooltip } from '@chronosphereio/chrono-ui';
import { isTimeRangeInputValueAbsolute } from '@chronosphereio/core';
import { Pin, PinOutline } from 'mdi-material-ui';
import { useState } from 'react';
import { CombinedControlsIconButton } from '@/components/CombinedControls';
import { useTimeZone } from '@/components/context';
import { type PinnedStatus, usePinnedTimeRangeStatus, useTimeRange } from '@/plugin-system/runtime/TimeRangeProviderV2';
import { useUserTimeFormat } from '@/plugin-system/runtime/useUserTimeFormat';
import { formatTimeRangeInputValue } from '../DateTimeRangePicker/utils';
import { PastPinIcon } from './PinPastIcon';

function getTooltipText(pinStatus: PinnedStatus) {
  if (pinStatus === 'pinned') {
    return 'Unpin time range';
  } else if (pinStatus === 'unpinned' || pinStatus === 'unknown') {
    return 'Pin time range';
  } else {
    return 'Use the time range currently pinned in another tab';
  }
}

export function PinButton() {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { absoluteTimeRange, timeRange } = useTimeRange();
  const { pin, unpin, pinnedStatus } = usePinnedTimeRangeStatus();

  const { timeZone } = useTimeZone();
  const timeFormat = useUserTimeFormat();

  const openMenu = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setAnchorEl(e.currentTarget);
    setIsMenuOpen(true);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setIsMenuOpen(false);
  };

  const isRelativeRangeSelected = !isTimeRangeInputValueAbsolute(timeRange);

  // close the menu if the range is pinned in another tab while the menu is open
  const [prevPinnedStatus, setPrevPinnedStatus] = useState(pinnedStatus);
  if (pinnedStatus !== prevPinnedStatus) {
    setPrevPinnedStatus(pinnedStatus);
    if (pinnedStatus === 'pinned-in-another-tab' && isMenuOpen) {
      closeMenu();
    }
  }

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // If pinned in another tab, pin in this tab
    if (pinnedStatus === 'pinned-in-another-tab') {
      pin();
      return;
    }

    // If time range is already pinned, unpin
    if (pinnedStatus === 'pinned') {
      unpin();
      return;
    }

    // Time range not pinned anywhere so either open the menu for relative ranges, or pin the absolute range
    if (isRelativeRangeSelected) {
      openMenu(e);
    } else {
      pin(timeRange);
    }
  };

  const tooltipText = getTooltipText(pinnedStatus);

  return (
    <>
      <Tooltip Content={tooltipText}>
        <CombinedControlsIconButton aria-label={tooltipText} onClick={handleClick} id="pin-button">
          <PinButtonIcon pinStatus={pinnedStatus} />
        </CombinedControlsIconButton>
      </Tooltip>

      <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={closeMenu} ariaLabel="pin time range menu">
        <MenuItem
          onClick={() => {
            closeMenu();
            pin(absoluteTimeRange);
          }}
          description={formatTimeRangeInputValue({ timeRange: absoluteTimeRange, timeFormat, timeZone })}
        >
          Pin with absolute time range
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            pin(timeRange);
          }}
          description={formatTimeRangeInputValue({ timeRange, timeFormat })}
        >
          Pin with relative time range
        </MenuItem>
      </Menu>
    </>
  );
}

interface PinButtonIconProps {
  pinStatus: PinnedStatus;
}

function PinButtonIcon({ pinStatus }: PinButtonIconProps) {
  if (pinStatus === 'pinned') {
    return (
      <Pin
        sx={{
          color: (theme) =>
            theme.featureFlags.panwRebrandStubYear
              ? theme.palette.interactive.highlight.default
              : theme.palette.primary.main,
        }}
      />
    );
  }

  if (pinStatus === 'pinned-in-another-tab') {
    return <PastPinIcon />;
  }

  return <PinOutline />;
}
